import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CircleNotch,
  Eye,
  EyeClosed,
  LockKey,
  ShieldCheck,
  UserCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";

const LOGIN_IMAGE_URL = process.env.REACT_APP_LOGIN_IMAGE_URL;

export default function LoginPage({ onLogin }) {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const updateField = (field, value) => {
    if (field === "user_id") {
      setUserId(value);
    } else {
      setPassword(value);
    }
    setFieldErrors((current) => ({ ...current, [field]: "" }));
    setError("");
  };

  const validate = () => {
    const nextErrors = {};
    if (!userId.trim()) {
      nextErrors.user_id = "Enter your User ID.";
    }
    if (!password) {
      nextErrors.password = "Enter your password.";
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { user_id: userId, password });
      onLogin(data.user);
      navigate("/");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page" data-testid="login-page">
      <section className="login-brand-panel" data-testid="login-brand-panel">
        <img
          className="login-brand-image"
          src={LOGIN_IMAGE_URL}
          alt="Modern blue glass architecture"
          data-testid="login-brand-image"
        />
        <div className="login-image-overlay" aria-hidden="true" />
        <div className="login-brand-content">
          <div className="login-brand" data-testid="login-brand">
            <span>CF</span> CrediFlow
          </div>
          <div className="login-brand-statement">
            <div className="eyebrow">Finance operations workspace</div>
            <h1>Clarity for every collection.</h1>
            <p>
              A focused internal workspace for loan servicing, repayment performance, and
              accountable team oversight.
            </p>
          </div>
          <div className="login-brand-footer" data-testid="login-brand-footer">
            <span>Private operations system</span>
            <span>Established workflow</span>
          </div>
        </div>
      </section>
      <section className="login-form-panel">
        <form onSubmit={submit} className="login-form" data-testid="login-form" noValidate>
          <header className="login-form-heading">
            <div className="internal-access-badge" data-testid="login-access-badge">
              <ShieldCheck size={16} /> CrediFlow Internal Access Only
            </div>
            <h2>Welcome back</h2>
            <p>Sign in to manage your loan operations workspace.</p>
          </header>
          <div className="login-field">
            <Label htmlFor="user-id">User ID</Label>
            <div className="login-input-wrap">
              <UserCircle size={19} aria-hidden="true" />
              <Input
                id="user-id"
                data-testid="login-user-id-input"
                className={fieldErrors.user_id ? "login-input-error" : ""}
                type="text"
                value={userId}
                onChange={(event) => updateField("user_id", event.target.value)}
                autoComplete="username"
                aria-invalid={Boolean(fieldErrors.user_id)}
                aria-describedby={fieldErrors.user_id ? "login-user-id-error" : undefined}
              />
            </div>
            {fieldErrors.user_id && (
              <div
                id="login-user-id-error"
                className="login-field-error"
                data-testid="login-user-id-error"
              >
                <WarningCircle size={16} /> {fieldErrors.user_id}
              </div>
            )}
          </div>
          <div className="login-field">
            <Label htmlFor="password">Password</Label>
            <div className="login-input-wrap login-password-wrap">
              <LockKey size={19} aria-hidden="true" />
              <Input
                id="password"
                data-testid="login-password-input"
                className={fieldErrors.password ? "login-input-error" : ""}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => updateField("password", event.target.value)}
                autoComplete="current-password"
                aria-invalid={Boolean(fieldErrors.password)}
                aria-describedby={fieldErrors.password ? "login-password-error" : undefined}
              />
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => setShowPassword((current) => !current)}
                data-testid="toggle-password-button"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeClosed size={19} /> : <Eye size={19} />}
              </button>
            </div>
            {fieldErrors.password && (
              <div
                id="login-password-error"
                className="login-field-error"
                data-testid="login-password-error"
              >
                <WarningCircle size={16} /> {fieldErrors.password}
              </div>
            )}
          </div>
          {error && (
            <div className="form-error" data-testid="login-error-message" role="alert">
              <WarningCircle size={17} /> {error}
            </div>
          )}
          <Button data-testid="login-submit-button" type="submit" disabled={loading}>
            {loading ? (
              <><CircleNotch size={18} className="login-spinner" /> Authenticating…</>
            ) : (
              <>Enter workspace <ArrowRight size={18} /></>
            )}
          </Button>
          <div className="secure-note" data-testid="login-security-note">
            <LockKey size={16} /> Protected internal workspace
          </div>
        </form>
      </section>
    </main>
  );
}